import React, { useContext, useCallback } from 'react';
import { FormattedMessage } from 'react-intl';
import { useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';

import { BannerStatus } from 'ui-components/components/banner-message/banner-enums';
import { BannerMessageV2 } from 'ui-components/components/banner-message/banner-message-v2';
import { Button } from 'ui-components/components/button';
import { Checkbox } from 'ui-components/components/checkbox';
import { LoadingIndicator } from 'ui-components/components/loading-indicator';

import AddShipFromButton from './add-ship-from-button';
import { DeliveryOptionsContext } from './context';
import { DeleteModal } from './delete-modal';
import { DeliveryOptionHeader } from './delivery-options-header';
import DeliveryOptionsRow from './delivery-options-row';
import style from './delivery-options-settings.module.less';
import ErrorContent from './error-content';
import { MagentoInvalidContent } from './magento-invalid-content';
import { WooCommerceInvalidContent } from './woocommerce-invalid-content';
import { PreviewDeliveryOptionsButton } from './preview-delivery-options-button';
import shopifyInvalidContent from './shopify-invalid-content';
import HelpArticleLink from '../../../../../components/help-article-link';
import ModalPortal from '../../../../../components/modal-portal';
import { StoreSettings, StoreSettingsGroupings } from '../../../../../enums/store-settings';
import { FeatureFlagDeclarations } from '../../../../../feature-flags/feature-flag-declarations';
import { useFeatureFlag } from '../../../../../feature-flags/hooks/use-feature-flag';
import { linkIDs } from '../../../../../services/link-service/links';
import Global from '../../../../../store/global-selectors';
import { useStoreUpdateContext } from '../context';
import messages from '../messages';
import sharedStyles from '../style.module.less';

const DeliveryOptionsSettings = () => {
  const {
    items,
    marketplaceName,
    error,
    storeGuid,
    isRegistered,
    registerStore,
    loading,
    shopifyIsValid,
    magentoIsValid,
    woocommerceIsValid,
    itemToRemove,
    onDeleteCancel,
    shouldUsePlanningToolRecommendationOnImport,
    setShouldUsePlanningToolRecommendationOnImport,
    contextMessages,
    isDeliveryMarketplaceAuctaneFulfillment,
  } = useContext(DeliveryOptionsContext);
  const history = useHistory();
  const onAddClickCallback = useCallback(() => {
    history.push(`/settings/stores/${storeGuid}/checkoutrates/add`);
  }, [history, storeGuid]);

  const shipFroms = useSelector(Global.selectSellerShipFroms);
  const defaultWarehouseName = useSelector(Global.selectSellerDefaultShipFrom)?.name ?? '';

  const { updateStoreSetting } = useStoreUpdateContext();
  const updateUsePlanningToolRecommendationOnImport = (value) => {
    setShouldUsePlanningToolRecommendationOnImport((bool) => !bool);
    updateStoreSetting(StoreSettingsGroupings.settings.key, StoreSettings.usePlanningToolRecommendationOnImport.key, value);
  };

  const isLiveRatesWithDimensionsEnabled = useFeatureFlag(FeatureFlagDeclarations.enableLiveRatesWithDimensions);
  const shouldShowSetShippingServiceSettingCheckbox =
    isRegistered && !isDeliveryMarketplaceAuctaneFulfillment && !isLiveRatesWithDimensionsEnabled;

  if (loading) {
    return (
      <div className={style['delivery-options-settings']}>
        <div className={style['loading-indicator-wrapper']}>
          <LoadingIndicator className={style['loading-indicator']} />
        </div>
      </div>
    );
  }

  if (!shopifyIsValid) {
    return (
      <div className={style['delivery-options-settings']}>
        <DeliveryOptionHeader defaultWarehouseName={defaultWarehouseName} marketplaceName={marketplaceName} />
        {shopifyInvalidContent}
      </div>
    );
  }

  if (!magentoIsValid) {
    return <MagentoInvalidContent defaultWarehouseName={defaultWarehouseName} marketplaceName={marketplaceName} />;
  }

  if (!woocommerceIsValid) {
    return <WooCommerceInvalidContent defaultWarehouseName={defaultWarehouseName} marketplaceName={marketplaceName} />;
  }

  if (shipFroms.length === 0) {
    return (
      <div className={style['delivery-options-settings']}>
        <div className={style['explanation-and-key']}>
          <div className={style.title}>
            <FormattedMessage {...messages.checkoutRates} />
          </div>
          <DeliveryOptionHeader defaultWarehouseName={defaultWarehouseName} marketplaceName={marketplaceName} />
        </div>
        <BannerMessageV2
          content={
            <FormattedMessage
              {...messages.setupShipFromsCR}
              values={{
                link: (children) => <AddShipFromButton>{children}</AddShipFromButton>,
              }}
            />
          }
          status={BannerStatus.Info}
        />
      </div>
    );
  }

  if (error) {
    return (
      <div className={style['delivery-options-settings']}>
        <ErrorContent errorMessage={error} />
      </div>
    );
  }

  return (
    <div className={style['delivery-options-settings']}>
      <div className={style['explanation-and-key']}>
        <div className={sharedStyles['tab-title']}>
          <FormattedMessage {...contextMessages.title} />
        </div>
        <DeliveryOptionHeader defaultWarehouseName={defaultWarehouseName} marketplaceName={marketplaceName} />
      </div>
      <div className={style['action-buttons-row']}>
        <h3>
          <FormattedMessage {...contextMessages.ratesHeader} />
        </h3>
        <div className={style.buttons}>
          <PreviewDeliveryOptionsButton />
          <Button
            disabled={!isRegistered}
            onClick={onAddClickCallback}
            pendoTrackingId='/settings/stores#add-delivery-option'
            theme='primary'
          >
            <FormattedMessage {...contextMessages.addRate} />
          </Button>
        </div>
      </div>
      <div className={style['delivery-options-list']}>
        <DeliveryOptionsRow isTitle />
        {!isRegistered && (
          <div className={style['list-placeholder']}>
            <h1>
              <FormattedMessage {...messages.haventConnectedMarketplaceTitle} values={{ marketplaceName }} />
            </h1>
            <p>
              <FormattedMessage {...messages.haventConnectedMarketplaceExplanationCR} values={{ marketplaceName }} />
            </p>
            <Button onClick={registerStore} theme='primary'>
              <FormattedMessage {...messages.connectToMarketplaceButton} values={{ marketplaceName }} />
            </Button>
          </div>
        )}
        {isRegistered && items.length === 0 ? (
          <div className={style['no-options-placeholder']}>
            <div>
              <FormattedMessage
                {...contextMessages.missingRatesMessage}
                values={{
                  a: (chunks) => (
                    <Button onClick={onAddClickCallback} pendoTrackingId='/settings/stores#add-delivery-option' theme='link'>
                      {chunks}
                    </Button>
                  ),
                }}
              />
            </div>
          </div>
        ) : null}
        {items.map((item, index) => (
          <DeliveryOptionsRow key={index} data={item} />
        ))}
        {shouldShowSetShippingServiceSettingCheckbox ? (
          <div className={style['service-setting-checkbox']}>
            <Checkbox
              checked={shouldUsePlanningToolRecommendationOnImport}
              label={<FormattedMessage {...messages.setCheckoutRatesServiceOnImport} />}
              onChange={updateUsePlanningToolRecommendationOnImport}
            >
              <div className={style['service-setting-checkbox-note']}>
                <FormattedMessage
                  {...messages.checkoutRatesServiceOnImportNote}
                  values={{
                    a: (chunks) => <HelpArticleLink articleId={linkIDs.ServiceMappingsKb}>{chunks}</HelpArticleLink>,
                  }}
                />
              </div>
            </Checkbox>
          </div>
        ) : null}
      </div>
      {itemToRemove ? (
        <ModalPortal onClose={onDeleteCancel}>
          <DeleteModal />
        </ModalPortal>
      ) : null}
    </div>
  );
};

export default DeliveryOptionsSettings;
